import { SpotlightCard } from "@aisquare/ui";
import { Section, VariantBlock } from "../lib/Section";

/**
 * Verification harness for the SpotlightCard surface — now wearing the Onyx
 * hex-notch silhouette. Verify in a real browser (shape() clip): the `nx-s`
 * notch traces the top-right chamfer, the `.elev-card` drop-shadow survives the
 * clip (no slice at the notch), the continuous `nx-rim` keyline rings the full
 * silhouette including the chamfer (a real border would be sliced there), and —
 * the SpotlightCard-specific check — the cursor-tracked radial glow stays
 * CLIPPED to the notch: move the pointer toward the top-right corner and the
 * glow must not bleed past the chamfer.
 */
export function SpotlightCardDemo() {
  return (
    <Section
      id="spotlight-card"
      title="SpotlightCard"
      description="A card surface with a cursor-following radial glow, now on the Onyx hex-notch silhouette (nx-s, the card scale): elevation is a clip-safe .elev-card drop-shadow — never a box-shadow, which the notch clip-path slices away — and the outline is a continuous nx-rim keyline ring, never a real border (sliced at the chamfer). overflow-hidden keeps the spotlight gradient clipped to the notch. Glow hue drives from theme tokens (primary | accent | secondary); intensity + spread tune the falloff; prefers-reduced-motion collapses the tracked glow to a static centered one."
    >
      <VariantBlock
        label="Default — primary glow"
        caption="hover + move the cursor; the glow stays clipped inside the notched silhouette"
      >
        <SpotlightCard className="max-w-xs">
          <h3 className="text-lg font-semibold text-foreground">Aiko's Studio</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Move your cursor across the card — the spotlight tracks it and stays
            inside the notch, including past the top-right chamfer.
          </p>
        </SpotlightCard>
      </VariantBlock>

      <VariantBlock
        label="Color — primary · accent · secondary"
        caption="glow hue drives from theme tokens, so per-studio retinting cascades in"
      >
        <SpotlightCard color="primary" className="max-w-[14rem]">
          <h3 className="text-base font-semibold text-foreground">Primary</h3>
          <p className="mt-1 text-sm text-muted-foreground">--primary glow</p>
        </SpotlightCard>
        <SpotlightCard color="accent" className="max-w-[14rem]">
          <h3 className="text-base font-semibold text-foreground">Accent</h3>
          <p className="mt-1 text-sm text-muted-foreground">--accent glow</p>
        </SpotlightCard>
        <SpotlightCard color="secondary" className="max-w-[14rem]">
          <h3 className="text-base font-semibold text-foreground">Secondary</h3>
          <p className="mt-1 text-sm text-muted-foreground">--secondary glow</p>
        </SpotlightCard>
      </VariantBlock>

      <VariantBlock
        label="Intensity — subtle · default · strong"
        caption="opacity of the glow; subtle for dense grids, strong for hero cards"
      >
        <SpotlightCard intensity="subtle" className="max-w-[14rem]">
          <h3 className="text-base font-semibold text-foreground">Subtle</h3>
          <p className="mt-1 text-sm text-muted-foreground">For dense tile grids.</p>
        </SpotlightCard>
        <SpotlightCard intensity="default" className="max-w-[14rem]">
          <h3 className="text-base font-semibold text-foreground">Default</h3>
          <p className="mt-1 text-sm text-muted-foreground">The standard step.</p>
        </SpotlightCard>
        <SpotlightCard intensity="strong" className="max-w-[14rem]">
          <h3 className="text-base font-semibold text-foreground">Strong</h3>
          <p className="mt-1 text-sm text-muted-foreground">For hero cards.</p>
        </SpotlightCard>
      </VariantBlock>

      <VariantBlock
        label="Padding — sm · lg · none"
        caption="the notched surface + keyline are constant; only the content inset changes"
      >
        <SpotlightCard padding="sm" className="max-w-[14rem]">
          <h3 className="text-base font-semibold text-foreground">Tight</h3>
          <p className="mt-1 text-sm text-muted-foreground">padding="sm"</p>
        </SpotlightCard>
        <SpotlightCard padding="lg" className="max-w-[14rem]">
          <h3 className="text-base font-semibold text-foreground">Roomy</h3>
          <p className="mt-1 text-sm text-muted-foreground">padding="lg"</p>
        </SpotlightCard>
      </VariantBlock>
    </Section>
  );
}
